import { Injectable } from '@angular/core';
import { BzgToastType } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { ShowToastService } from 'app/shared/services/showToast.service';

export interface ToastConfig {
  type: BzgToastType;
  duration?: number;
  verticalPosition?: 'top' | 'bottom';
  horizontalPosition?: 'left' | 'center' | 'right';
  showCountDown?: boolean;
  fullwidth?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private readonly defaultConfig: Partial<ToastConfig> = {
    verticalPosition: 'bottom',
    horizontalPosition: 'right',
    showCountDown: false,
    fullwidth: false
  };

  constructor(
    private _translateService: TranslateService,
    private _showToastService: ShowToastService
  ) { }

  showSuccess(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'success');
  }

  showError(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'error');
  }

  showInfo(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'info');
  }

  showWarning(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'warning');
  }

  showLocalizedToast(
    messageKey: string, 
    messageParams?: string[], 
    config?: Partial<ToastConfig>
  ): void {
    const message = this.translateMessageWithParams(messageKey, messageParams);
    const finalConfig = { ...this.defaultConfig, ...config };
    this._showToastService.showToast(message, 'success');
  }

  private translateMessageWithParams(messageKey: string, messageParams?: string[]): string {
    let message = this._translateService.instant(messageKey);
    
    if (messageParams) {
      messageParams.forEach((param, index) => {
        message = message.replace(`{${index}}`, param);
      });
    }
    
    return message;
  }

  private parseErrorMessage(errorMessage: string): { key: string; param?: string } {
    const errorParts = errorMessage.split('|');
    return {
      key: errorParts[0] || 'BASEKNOWLEDGECATALOG.ERRORS.ERROR_READING_FILE',
      param: errorParts[1]
    };
  }

  showInvalidFilesError(invalidFiles: any[]): void {
    const errorMessages = invalidFiles.map(file => {
      const { key, param } = this.parseErrorMessage(file.error);
      const translatedError = this.translateMessageWithParams(key, param ? [param] : undefined);
      return `${file.file.name}: ${translatedError}`;
    });
    
    const invalidFilesTitle = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.INVALID_FILES_FOUND');
    this.showError(`<b>${invalidFilesTitle}</b>: <em>${errorMessages.join(', ')}</em>`);
  }

  showFileConversionError(error: any): void {
    const errorMsg = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.ERROR_CONVERTING_FILES');
    this.showError(`<b>${errorMsg}</b>: <em>${error.message}</em>`);
  }

  showMaxFilesExceededError(errorMessage: string): void {
    const { key, param } = this.parseErrorMessage(errorMessage);
    const translatedError = this.translateMessageWithParams(key, param ? [param] : undefined);
    
    this.showError(`<b>${translatedError}</b>`);
  }
}
